// Car Details Component
const CarDetails = {
	props: {
		modelValue: {
			type: Object,
			required: true
		},
		validationErrors: {
			type: Object,
			default: () => ({})
		}
	},
	emits: ['update:modelValue'],
	data() {
		return {
			i18n: mmkBookingData.i18n || {}
		};
	},
	template: `
        <div class="mmk-booking-form__section mmk-booking-form__section--car" :class="{'mmk-booking-form__section--error': validationErrors.sections.includes('car')}">
            <div class="row">
                <div class="small-12 large-6 column">
                    <div class="row">
                        <div class="small-12 column">
                            <label class="mmk-booking-form__label">{{ i18n.vehicle }}</label>
                        </div>
                        <div class="small-12 columns">
                            <div class="mmk-booking-form__input-group">
                                <input type="text" 
                                       :value="modelValue.plateNr"
                                       @input="updateField('plateNr', $event.target.value)"
                                       placeholder="" 
                                       autocomplete="license-plate"
                                       :class="['mmk-booking-form__text-input', modelValue.plateNr ? 'has-content' : '']">
                                <label class="mmk-booking-form__floating-label">{{ i18n.plate_number }}</label>
                            </div>
                        </div>
                        <div class="small-12 columns">
                            <div class="mmk-booking-form__input-group">
                                <input type="text" 
                                       :value="modelValue.brand"
                                       @input="updateField('brand', $event.target.value)"
                                       placeholder="" 
                                       autocomplete="car-brand"
                                       :class="['mmk-booking-form__text-input', modelValue.brand ? 'has-content' : '']">
                                <label class="mmk-booking-form__floating-label">{{ i18n.brand }}</label>
                            </div>
                        </div>
                        <div class="small-12 columns">
                            <div class="mmk-booking-form__input-group">
                                <input type="text" 
                                       :value="modelValue.model"
                                       @input="updateField('model', $event.target.value)"
                                       placeholder="" 
                                       autocomplete="car-model"
                                       :class="['mmk-booking-form__text-input', modelValue.model ? 'has-content' : '']">
                                <label class="mmk-booking-form__floating-label">{{ i18n.model }}</label>
                            </div>
                        </div>
                        <div class="small-12 columns">
                            <div class="mmk-booking-form__input-group">
                                <input type="text" 
                                       :value="modelValue.year"
                                       @input="updateField('year', $event.target.value)"
                                       placeholder="" 
                                       autocomplete="car-year"
                                       :class="['mmk-booking-form__text-input', modelValue.year ? 'has-content' : '']">
                                <label class="mmk-booking-form__floating-label">{{ i18n.year }}</label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
	methods: {
		updateField(field, value) {
			const updatedValue = {...this.modelValue};
			updatedValue[field] = value;
			this.$emit('update:modelValue', updatedValue);
		}
	}
};

// Diagonal Line Component
const DiagonalLine = {
	template: `
        <svg class="diagonal-line" width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="0" y1="100" x2="100" y2="0" stroke="#D9D9D9" stroke-width="2"/>
        </svg>
    `
};

// Booking Calendar Component
const BookingCalendar = {
	props: {
		modelValue: {
			type: Object,
			required: true
		},
		availableSlots: {
			type: Object,
			default: () => ({})
		},
		disabledDates: {
			type: Array,
			default: () => []
		}
	},
	emits: ['update:modelValue'],
	data() {
		return {
			currentDate: new Date(),
			selectedDate: null,
			availableMonths: [],
			weekView: true,
			currentWeekOffset: 0,
			maxWeeks: 4, // Current week + 3 weeks ahead
			fullMonthView: false, // Flag to indicate if we're showing a full month
			viewingMonth: null, // Month being viewed in full month mode
			i18n: mmkBookingData.i18n || {}
		}
	},
	watch: {
		'modelValue.date': {
			immediate: true,
			handler(newDate) {
				if (newDate) {
					this.selectedDate = newDate;
				}
			}
		}
	},
	computed: {
		weekdayNames() {
			// Return translated weekday names
			return [
				this.i18n.weekday_1 || 'P', // Monday
				this.i18n.weekday_2 || 'O', // Tuesday
				this.i18n.weekday_3 || 'T', // Wednesday
				this.i18n.weekday_4 || 'C', // Thursday
				this.i18n.weekday_5 || 'P', // Friday
				this.i18n.weekday_6 || 'S', // Saturday
				this.i18n.weekday_7 || 'Sv'  // Sunday
			];
		},
		currentMonth() {
			return this.currentDate.getMonth();
		},
		currentYear() {
			return this.currentDate.getFullYear();
		},
		monthName() {
			// Get month number (1-12) and return the corresponding translated month name
			const monthNum = this.currentMonth + 1; // JavaScript months are 0-indexed
			return this.i18n[`month_${monthNum}`] || new Date(this.currentYear, this.currentMonth).toLocaleString('lv', {month: 'long'});
		},
		monthKey() {
			return `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}`;
		},
		calendarDays() {
			if (!this.weekView) {
				// Original month view logic
				const firstDay = new Date(this.currentYear, this.currentMonth, 1);
				const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
				const days = [];

				// Add empty cells for days before the first day of the month
				// getDay() returns 0 for Sunday, but we want Monday to be 0
				const firstDayOfWeek = firstDay.getDay() || 7;
				for (let i = 1; i < firstDayOfWeek; i++) {
					days.push({day: '', disabled: true, empty: true});
				}

				// Add days of the month
				for (let i = 1; i <= lastDay.getDate(); i++) {
					const date = new Date(this.currentYear, this.currentMonth, i);
					const dateStr = this.formatDate(date);
					days.push({
						day: i,
						date: dateStr,
						disabled: this.isDisabled(date),
						availableSlots: this.availableSlots[dateStr] || 0,
						isToday: this.isToday(date),
						isSelected: this.selectedDate === dateStr
					});
				}

				return days;
			} else if (this.fullMonthView && this.viewingMonth) {
				// Full month view logic
				const [year, month] = this.viewingMonth.split('-').map(Number);
				const firstDay = new Date(year, month - 1, 1);
				const lastDay = new Date(year, month - 1 + 1, 0);
				const days = [];

				// Add month header
				days.push({
					type: 'monthHeader',
					month: month - 1,
					year: year,
					key: this.viewingMonth,
					monthName: this.i18n[`month_${month}`] || firstDay.toLocaleString('lv', {month: 'long'})
				});

				// Add empty cells for days before the first day of the month
				const firstDayOfWeek = firstDay.getDay() || 7; // Convert Sunday (0) to 7
				for (let i = 1; i < firstDayOfWeek; i++) {
					days.push({
						day: '',
						disabled: true,
						empty: true
					});
				}

				// Add days of the month
				for (let i = 1; i <= lastDay.getDate(); i++) {
					const date = new Date(year, month - 1, i);
					const dateStr = this.formatDate(date);
					days.push({
						day: i,
						date: dateStr,
						disabled: this.isDisabled(date),
						availableSlots: this.availableSlots[dateStr] || 0,
						isToday: this.isToday(date),
						isSelected: this.selectedDate === dateStr,
						weekNumber: this.getWeekOfMonth(date)
					});
				}

				return days;
			} else {
				// Week view logic
				const today = new Date();
				today.setHours(0, 0, 0, 0);

				// Find the start of the current week (Monday)
				const startOfCurrentWeek = new Date(today);
				const dayOfWeek = today.getDay() || 7; // Convert Sunday (0) to 7
				startOfCurrentWeek.setDate(today.getDate() - (dayOfWeek - 1) + (this.currentWeekOffset * 7));

				const days = [];
				const weeksToShow = this.maxWeeks;

				// Group days by month for display
				let currentMonthKey = null;

				// Generate days for the requested weeks
				for (let week = 0; week < weeksToShow; week++) {
					const weekStart = new Date(startOfCurrentWeek);
					weekStart.setDate(startOfCurrentWeek.getDate() + (week * 7));

					// Process each day in the week
					for (let i = 0; i < 7; i++) {
						const date = new Date(weekStart);
						date.setDate(weekStart.getDate() + i);
						const monthYearKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

						// If we're starting a new month, add a month header
						if (monthYearKey !== currentMonthKey) {
							days.push({
								type: 'monthHeader',
								month: date.getMonth(),
								year: date.getFullYear(),
								key: monthYearKey,
								monthName: this.i18n[`month_${date.getMonth() + 1}`] || date.toLocaleString('lv', {month: 'long'})
							});

							// If this is the first day of a month and not Monday, add empty cells
							if (date.getDate() === 1) {
								const firstDayOfWeek = date.getDay() || 7; // Convert Sunday (0) to 7
								for (let j = 1; j < firstDayOfWeek; j++) {
									days.push({
										day: '',
										disabled: true,
										empty: true
									});
								}
							}

							currentMonthKey = monthYearKey;
						}

						const dateStr = this.formatDate(date);
						days.push({
							day: date.getDate(),
							date: dateStr,
							disabled: this.isDisabled(date),
							availableSlots: this.availableSlots[dateStr] || 0,
							isToday: this.isToday(date),
							isSelected: this.selectedDate === dateStr,
							weekNumber: this.getWeekOfMonth(date)
						});
					}
				}

				return days;
			}
		},
		canGoNext() {
			if (!this.weekView) {
				const maxDate = new Date();
				maxDate.setMonth(maxDate.getMonth() + 3);
				return new Date(this.currentYear, this.currentMonth) < maxDate;
			} else if (this.fullMonthView) {
				// For full month view, check if we can go to the next month
				const [year, month] = this.viewingMonth.split('-').map(Number);
				const nextMonthDate = new Date(year, month, 1); // month is already 1-based

				const maxDate = new Date();
				maxDate.setMonth(maxDate.getMonth() + 3);

				return nextMonthDate <= maxDate;
			} else {
				// For week view, allow going forward if we haven't reached max weeks ahead
				const maxWeeksAhead = 12; // 3 months ~ 12 weeks
				return this.currentWeekOffset < maxWeeksAhead;
			}
		},
		canGoPrev() {
			if (!this.weekView) {
				// Original logic
				return !(this.currentMonth === new Date().getMonth() &&
					this.currentYear === new Date().getFullYear());
			} else if (this.fullMonthView) {
				// For full month view, check if we can go to the previous month
				const [year, month] = this.viewingMonth.split('-').map(Number);
				const prevMonthDate = new Date(year, month - 2, 1); // month - 1 for 0-based, -1 more for prev month

				const today = new Date();
				const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

				return prevMonthDate >= currentMonthStart;
			} else {
				// For week view, can go back if we're not at the current week
				return this.currentWeekOffset > 0;
			}
		}
	},
	created() {
		// Generate available months based on daysToOffer setting
		const now = new Date();
		const months = [];
		const daysToOffer = Number(window.mmkBookingData.daysToOffer || 90);
		
		// Calculate how many months to show based on daysToOffer
		// Add 1 to include the current month, and Math.ceil to round up
		const monthsToShow = Math.ceil(daysToOffer / 30) + 1;
		
		for (let i = 0; i < monthsToShow; i++) {
			const date = new Date(now.getFullYear(), now.getMonth() + i);
			const monthNum = date.getMonth() + 1; // JavaScript months are 0-indexed
			const translatedMonth = this.i18n[`month_${monthNum}`] || date.toLocaleString('lv', {month: 'long'});
			months.push({
				key: `${date.getFullYear()}-${String(monthNum).padStart(2, '0')}`,
				label: `${translatedMonth} ${date.getFullYear()}`,
				month: date.getMonth(),
				year: date.getFullYear()
			});
		}

		this.availableMonths = months;

		// Check if we're in the first week of the month
		const today = new Date();
		const dayOfMonth = today.getDate();
		const dayOfWeek = today.getDay() || 7; // Convert Sunday (0) to 7

		// If we're in the first week of the month (date <= 7), show full month view instead of split view
		if (dayOfMonth <= 7) {
			this.fullMonthView = true;
			this.viewingMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
		}
	},
	methods: {
		prevMonth() {
			if (!this.weekView) {
				const newDate = new Date(this.currentYear, this.currentMonth - 1);
				if (newDate >= new Date()) {
					this.currentDate = newDate;
				}
			} else if (this.fullMonthView) {
				// In full month view, go to previous month
				const [year, month] = this.viewingMonth.split('-').map(Number);
				const prevMonth = month - 1;
				const prevYear = prevMonth === 0 ? year - 1 : year;
				const prevMonthKey = `${prevYear}-${String(prevMonth === 0 ? 12 : prevMonth).padStart(2, '0')}`;

				// Check if previous month is not before current month
				const today = new Date();
				const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

				if (prevMonthKey >= currentMonthKey) {
					this.viewingMonth = prevMonthKey;
				} else {
					// If trying to go before current month, switch back to week view
					this.fullMonthView = false;
					this.currentWeekOffset = 0;
				}
			} else {
				// For week view, go back one week
				if (this.currentWeekOffset > 0) {
					this.currentWeekOffset--;
				}
			}
		},
		nextMonth() {
			if (!this.weekView) {
				const newDate = new Date(this.currentYear, this.currentMonth + 1);
				const maxDate = new Date();
				maxDate.setMonth(maxDate.getMonth() + 3);

				if (newDate <= maxDate) {
					this.currentDate = newDate;
				}
			} else if (this.fullMonthView) {
				// In full month view, go to next month
				const [year, month] = this.viewingMonth.split('-').map(Number);
				const nextMonth = month + 1;
				const nextYear = nextMonth > 12 ? year + 1 : year;
				const nextMonthKey = `${nextYear}-${String(nextMonth > 12 ? 1 : nextMonth).padStart(2, '0')}`;

				// Check if next month is not more than 3 months ahead
				const maxDate = new Date();
				maxDate.setMonth(maxDate.getMonth() + 3);
				const maxMonthKey = `${maxDate.getFullYear()}-${String(maxDate.getMonth() + 1).padStart(2, '0')}`;

				if (nextMonthKey <= maxMonthKey) {
					this.viewingMonth = nextMonthKey;
				}
			} else {
				// For week view, check if we're showing a partial month
				const today = new Date();
				const startOfCurrentWeek = new Date(today);
				const dayOfWeek = today.getDay() || 7;
				startOfCurrentWeek.setDate(today.getDate() - (dayOfWeek - 1) + (this.currentWeekOffset * 7));

				// Check if any of the days in the current view are in a different month
				// than the last day of the current view
				const lastDayOfView = new Date(startOfCurrentWeek);
				lastDayOfView.setDate(startOfCurrentWeek.getDate() + (this.maxWeeks * 7) - 1);

				// Get the month of the last day
				const lastMonth = lastDayOfView.getMonth();
				const lastYear = lastDayOfView.getFullYear();

				// Check if there's a partial month in the view
				let hasPartialNextMonth = false;
				const currentViewDays = this.calendarDays;

				// Find all month headers
				const monthHeaders = currentViewDays.filter(item => item.type === 'monthHeader');

				if (monthHeaders.length > 1) {
					// There's at least one partial month, switch to full month view of the last month
					const lastMonthHeader = monthHeaders[monthHeaders.length - 1];
					this.fullMonthView = true;
					this.viewingMonth = lastMonthHeader.key;
				} else {
					// No partial month, just advance the week offset
					const maxWeeksAhead = 12; // 3 months ~ 12 weeks
					if (this.currentWeekOffset < maxWeeksAhead) {
						this.currentWeekOffset++;
					}
				}
			}
		},
		selectMonth(monthKey) {
			if (this.weekView) {
				// Switch to full month view
				this.fullMonthView = true;
				this.viewingMonth = monthKey;
			} else {
				// Original logic
				const [year, month] = monthKey.split('-').map(Number);
				this.currentDate = new Date(year, month - 1);
			}
		},
		getWeekOfMonth(date) {
			const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
			const firstWeekday = firstDayOfMonth.getDay() || 7;
			const offsetDate = date.getDate() + firstWeekday - 1;
			return Math.floor(offsetDate / 7) + 1;
		},
		selectDate(day) {
			// Don't do anything for empty days or days without a date
			if (!day.day || day.empty) {
				return;
			}

			// Only allow selection if the day is not disabled and has available slots
			if (!day.disabled && day.availableSlots > 0) {
				this.selectedDate = day.date;
				const updatedValue = {...this.modelValue};
				updatedValue.date = day.date;
				this.$emit('update:modelValue', updatedValue);
			}
		},
		formatDate(date) {
			// Format date in local timezone to match PHP's format('Y-m-d')
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			return `${year}-${month}-${day}`;
		},
		isDisabled(date) {
			// Check if date is in the past
			const today = new Date();
			today.setHours(0, 0, 0, 0);

			if (date < today) return true;

			// Check if date is manually disabled
			const dateStr = this.formatDate(date);
			if (this.disabledDates.includes(dateStr)) return true;

			return false;
		},
		isHoliday(date) {
			// Check if we have holidays data
			if (!mmkBookingData.holidays) return false;

			const dateStr = this.formatDate(date);

			// Check single dates
			const singleDates = mmkBookingData.holidays.single_dates || [];
			if (singleDates.includes(dateStr)) return true;

			// Check recurring dates (MM-DD format)
			const recurringDates = mmkBookingData.holidays.recurring_dates || [];
			const monthDay = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

			if (recurringDates.includes(monthDay)) return true;

			return false;
		},
		isToday(date) {
			const today = new Date();
			return date.getDate() === today.getDate() &&
				date.getMonth() === today.getMonth() &&
				date.getFullYear() === today.getFullYear();
		}
	},
	components: {
		DiagonalLine
	},
	template: `
        <div class="mmk-booking-calendar">
            <div class="mmk-booking-calendar__header">
                <select :value="fullMonthView ? viewingMonth : monthKey" 
                        @change="selectMonth($event.target.value)"
                        class="mmk-booking-calendar__month-select">
                    <option v-for="month in availableMonths" 
                            :key="month.key"
                            :value="month.key">
                        {{ month.label }}
                    </option>
                </select>
                
                <div class="mmk-booking-calendar__header-nav-buttons">
                    <button type="button"
                            @click="prevMonth" 
                            :disabled="!canGoPrev"
                            class="mmk-booking-calendar__nav-btn mmk-booking-calendar__nav-btn--left">
                    </button>
                    <button type="button"
                            @click="nextMonth" 
                            :disabled="!canGoNext"
                            class="mmk-booking-calendar__nav-btn mmk-booking-calendar__nav-btn--right">
                    </button>
                </div>   
            </div>
            <div class="mmk-booking-calendar__days">
                <template v-if="weekView">
                    <div v-for="(item, index) in calendarDays" 
                         :key="index"
                         :class="[
                             {'mmk-booking-calendar__month-header': item.type === 'monthHeader'},
                             {'mmk-booking-calendar__day': !item.type},
                             {
                                 'mmk-booking-calendar__day--disabled': !item.type && (item.disabled || item.availableSlots <= 0) && !item.empty,
                                 'mmk-booking-calendar__day--empty': !item.type && item.empty,
                                 'mmk-booking-calendar__day--today': !item.type && item.isToday,
                                 'mmk-booking-calendar__day--selected': !item.type && item.isSelected,
                                 'mmk-booking-calendar__day--holiday': !item.type && item.date && this.isHoliday(new Date(item.date))
                             }
                         ]"
                         @click="!item.type && selectDate(item)">
                        <template v-if="item.type === 'monthHeader'">
							<div class="mmk-booking-calendar__month-name-container">
                                <h3 class="mmk-booking-calendar__month-name">{{ item.monthName }} {{ item.year }}</h3>
							</div>   
                            <div class="mmk-booking-calendar__weekdays-row">
                                <div v-for="(day, dayIndex) in weekdayNames" 
                                     :key="dayIndex" 
                                     class="mmk-booking-calendar__weekday">
                                    {{ day }}
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <span class="mmk-booking-calendar__day-number">{{ item.day }}</span>
                            <span v-if="item.availableSlots && !item.disabled" 
                                  :class="['mmk-booking-calendar__slots', {'mmk-booking-calendar__slots--last-one': item.availableSlots === 1}]">
                                {{ item.availableSlots }} <span class="show-for-large">{{ item.availableSlots === 1 ? (i18n.slot_singular || 'vieta') : (i18n.slot_plural || 'vietas') }}</span>
                            </span>
                            <span v-else-if="item.date && this.isHoliday(new Date(item.date))" class="mmk-booking-calendar__slots mmk-booking-calendar__slots--holiday">
                                {{ i18n.holiday || 'Brīvdiena' }}
                            </span>
                            <span v-else-if="item.day" class="mmk-booking-calendar__slots mmk-booking-calendar__slots--empty">
                            </span>
                            <DiagonalLine v-if="(item.disabled||item.availableSlots<=0) && item.day" />
                        </template>
                    </div>
                </template>
                <template v-else-if="fullMonthView">
                    <div v-for="(item, index) in calendarDays" 
                         :key="index"
                         :class="[
                             {'mmk-booking-calendar__month-header': item.type === 'monthHeader'},
                             {'mmk-booking-calendar__day': !item.type},
                             {
                                 'mmk-booking-calendar__day--disabled': !item.type && (item.disabled || item.availableSlots <= 0) && !item.empty,
                                 'mmk-booking-calendar__day--empty': !item.type && item.empty,
                                 'mmk-booking-calendar__day--today': !item.type && item.isToday,
                                 'mmk-booking-calendar__day--selected': !item.type && item.isSelected,
                                 'mmk-booking-calendar__day--holiday': !item.type && item.date && this.isHoliday(new Date(item.date))
                             }
                         ]"
                         @click="!item.type && selectDate(item)">
                        <template v-if="item.type === 'monthHeader'">
							<div class="mmk-booking-calendar__month-name-container">
                                <h3 class="mmk-booking-calendar__month-name">{{ item.monthName }} {{ item.year }}</h3>
							</div>  
                            <div class="mmk-booking-calendar__weekdays-row">
                                <div v-for="(day, dayIndex) in weekdayNames" 
                                     :key="dayIndex" 
                                     class="mmk-booking-calendar__weekday">
                                    {{ day }}
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <span class="mmk-booking-calendar__day-number">{{ item.day }}</span>
                            <span v-if="item.availableSlots && !item.disabled" 
                                  :class="['mmk-booking-calendar__slots', {'mmk-booking-calendar__slots--last-one': item.availableSlots === 1}]">
                                {{ item.availableSlots }} <span class="show-for-large">{{ item.availableSlots === 1 ? (i18n.slot_singular || 'vieta') : (i18n.slot_plural || 'vietas') }}</span>
                            </span>
                            <span v-else-if="item.date && this.isHoliday(new Date(item.date))" class="mmk-booking-calendar__slots mmk-booking-calendar__slots--holiday">
                                {{ i18n.holiday || 'Brīvdiena' }}
                            </span>
                            <span v-else-if="item.day" class="mmk-booking-calendar__slots mmk-booking-calendar__slots--empty">
                            </span>
                            <DiagonalLine v-if="!item.type && (item.disabled || item.availableSlots <= 0) && item.day" />
                        </template>
                    </div>
                </template>
                <template v-else>
                    <div class="mmk-booking-calendar__weekdays-row">
                        <div v-for="(day, dayIndex) in weekdayNames" 
                             :key="dayIndex" 
                             class="mmk-booking-calendar__weekday">
                            {{ day }}
                        </div>
                    </div>
                    <div v-for="(day, index) in calendarDays" 
                         :key="index"
                         @click="selectDate(day)"
                         :class="[
                             'mmk-booking-calendar__day',
                             {
                                 'mmk-booking-calendar__day--disabled': day.disabled||day.availableSlots<=0,
                                 'mmk-booking-calendar__day--today': day.isToday,
                                 'mmk-booking-calendar__day--selected': day.isSelected,
                                 'mmk-booking-calendar__day--empty': !day.day,
                                 'mmk-booking-calendar__day--holiday': day.date && this.isHoliday(new Date(day.date))
                             }
                         ]">
                        <span class="mmk-booking-calendar__day-number">{{ day.day }}</span>
                        <span v-if="day.availableSlots && !day.disabled" 
                              :class="['mmk-booking-calendar__slots', {'mmk-booking-calendar__slots--last-one': day.availableSlots === 1}]">
                            {{ day.availableSlots }} <span class="show-for-large">{{ day.availableSlots === 1 ? 'vieta' : 'vietas' }}</span>
                        </span>
                        <span v-else-if="day.date && this.isHoliday(new Date(day.date))" class="mmk-booking-calendar__slots mmk-booking-calendar__slots--holiday">
                            Brīvdiena
                        </span>
                        <span v-else-if="day.day" class="mmk-booking-calendar__slots mmk-booking-calendar__slots mmk-booking-calendar__slots--empty">
                        </span>
						
                        <DiagonalLine v-if="(day.disabled||day.availableSlots<=0) && !day.empty" />
                    </div>
                </template>
            </div>
        </div>
    `
};

// Time Slots Component
const TimeSlots = {
	props: {
		modelValue: {
			type: Object,
			required: true
		},
		availableSlots: {
			type: Array,
			default: () => []
		},
		currentBranchBoxes: {
			type: Array,
			default: () => []
		}
	},
	emits: ['update:modelValue', 'refresh-availability'],
	data() {
		return {
			i18n: mmkBookingData.i18n || {},
			canManage: !!(window.mmkBookingData && window.mmkBookingData.canManage),
			adminModeOn: false,
			overrideSelections: {},
			editOverride: {
				visible: false,
				slot: null,
				selected: [],
				saving: false,
				error: ''
			}
		};
	},
	computed: {
		formattedSlots() {
			// Get current date and time
			const now = new Date();

			// Use configured minimum lead time (hours) from backend, default to 2 if not set
			let disabledFutureHoursCount = parseInt((mmkBookingData && mmkBookingData.minHoursAhead), 10);
			if (isNaN(disabledFutureHoursCount)) {
				disabledFutureHoursCount = 2;
			}

			const minTime = new Date(now.getTime() + (disabledFutureHoursCount * 60 * 60 * 1000));

			return this.availableSlots.map(slot => {
				// Parse the time from the slot
				const [hours, minutes] = slot.time.split(':').map(Number);

				// Create a date object for the slot time on the selected date
				const slotDate = new Date(this.modelValue.date);
				slotDate.setHours(hours, minutes, 0, 0);

				// Check if the slot is in the past or within the next 2 hours
				const isPastOrTooSoon = slotDate < minTime;

				return {
					value: slot.time,
					label: slot.time,
					disabled: slot.available_boxes === 0 || isPastOrTooSoon,
					boxes: slot.available_boxes,
					totalBoxes: slot.total_boxes,
					isPastOrTooSoon: isPastOrTooSoon,
					boxIds: slot.boxIds || [],
					originalBoxIds: slot.original_box_ids || slot.boxIds || [],
					closedBoxIds: slot.closed_box_ids || [],
					overrideApplied: !!slot.override_applied
				};
			});
		},
		safeBranchBoxes() {
			// Ensure we iterate only over valid box objects for the current branch
			if (!Array.isArray(this.currentBranchBoxes)) return [];
			return this.currentBranchBoxes.filter(b => b && typeof b === 'object' && ('id' in b));
		},
		boxesForOverrideSlot() {
			// Filter branch boxes to only those applicable for the selected slot (includes both open and closed)
			if (!this.editOverride.slot) return [];
			const ids = Array.isArray(this.editOverride.slot.originalBoxIds)
				? this.editOverride.slot.originalBoxIds.map(id => String(id))
				: [];
			const selectedService = this.modelValue && this.modelValue.service ? String(this.modelValue.service) : null;
			return this.safeBranchBoxes.filter(b => {
				if (!ids.includes(String(b.id))) return false;
				const boxServices = Array.isArray(b.services) ? b.services.map(s => String(s)) : [];
				return selectedService ? boxServices.includes(selectedService) : true;
			});
		}
	},
	methods: {
		boxesForSlot(slot) {
			if (!slot || !Array.isArray(slot.originalBoxIds)) return [];
			const ids = slot.originalBoxIds.map(id => String(id));
			const selectedService = this.modelValue && this.modelValue.service ? String(this.modelValue.service) : null;
			return this.safeBranchBoxes.filter(b => {
				if (!ids.includes(String(b.id))) return false;
				const boxServices = Array.isArray(b.services) ? b.services.map(s => String(s)) : [];
				return selectedService ? boxServices.includes(selectedService) : true;
			});
		},
		selectionFor(slot) {
			const key = slot && slot.value ? String(slot.value) : '';
			if (!key) return [];
			const arr = this.overrideSelections[key];
			if (Array.isArray(arr)) return arr;
			// Fallback to current closed boxes
			return Array.isArray(slot.closedBoxIds) ? slot.closedBoxIds.map(String) : [];
		},
		setSelectionFor(slot, ids) {
			const key = slot && slot.value ? String(slot.value) : '';
			if (!key) return;
			this.overrideSelections[key] = Array.isArray(ids) ? ids.map(String) : [];
		},
		toggleBox(id, slot) {
			const current = this.selectionFor(slot);
			const idx = current.indexOf(String(id));
			if (idx >= 0) {
				current.splice(idx, 1);
			} else {
				current.push(String(id));
			}
			this.setSelectionFor(slot, current);
		},
		closeAll(slot) {
			const ids = Array.isArray(slot?.originalBoxIds) ? slot.originalBoxIds.map(String) : [];
			this.setSelectionFor(slot, ids);
		},
		openAll(slot) {
			this.setSelectionFor(slot, []);
		},
		async saveAllSlotOverrides(){
			if (!this.canManage) return;
			this.editOverride.saving = true;
			this.editOverride.error = '';
			try {
				const normalize = (arr) => Array.from(new Set((arr || []).map(String))).sort();
				const isEqual = (a, b) => {
					const A = normalize(a);
					const B = normalize(b);
					if (A.length !== B.length) return false;
					for (let i = 0; i < A.length; i++) {
						if (A[i] !== B[i]) return false;
					}
					return true;
				};

				// Collect changed slots only
				const changedSlots = [];
				for (const slot of this.formattedSlots) {
					if (!slot || !slot.value) continue;
					const currentSel = this.selectionFor(slot);
					const originalSel = Array.isArray(slot.closedBoxIds) ? slot.closedBoxIds.map(String) : [];
					if (!isEqual(currentSel, originalSel)) {
						changedSlots.push({ time: slot.value, closed_box_ids: currentSel });
					}
				}

				if (changedSlots.length === 0) {
					this.editOverride.saving = false;
					return; // nothing to save
				}

				const payload = {
					service_id: this.modelValue.service,
					branch_id: this.modelValue.department,
					date: this.modelValue.date,
					slots: changedSlots
				};

				const formData = new FormData();
				formData.append('action', 'mmk_booking_save_overrides_bulk');
				formData.append('nonce', window.mmkBookingData.adminNonce);
				formData.append('data', JSON.stringify(payload));

				const response = await fetch(window.mmkBookingData.ajaxUrl, { method: 'POST', body: formData });
				const data = await response.json();
				if (!data || !data.success) {
					throw new Error((data && data.data && data.data.message) ? data.data.message : 'Failed to save overrides');
				}

				this.$emit('refresh-availability');
			} catch (e) {
				console.error('Error saving all overrides:', e);
				this.editOverride.error = String(e.message || e);
			} finally {
				this.editOverride.saving = false;
			}
		},
		async saveSlotOverride(slot) {
			if (!this.canManage || !slot) return;
			this.editOverride.saving = true;
			this.editOverride.error = '';
			try {
				const formData = new FormData();
				formData.append('action', 'mmk_booking_save_overrides');
				formData.append('nonce', window.mmkBookingData.adminNonce);
				formData.append('service_id', this.modelValue.service);
				formData.append('branch_id', this.modelValue.department);
				formData.append('date', this.modelValue.date);
				formData.append('time', slot.value);
				(this.selectionFor(slot) || []).forEach(id => formData.append('closed_box_ids[]', id));

				const response = await fetch(window.mmkBookingData.ajaxUrl, { method: 'POST', body: formData });
				const data = await response.json();
				if (!data || !data.success) {
					throw new Error((data && data.data && data.data.message) ? data.data.message : 'Failed to save overrides');
				}

				this.$emit('refresh-availability');
			} catch (e) {
				console.error('Error saving overrides:', e);
				this.editOverride.error = String(e.message || e);
			} finally {
				this.editOverride.saving = false;
			}
		},
		selectTime(time) {
			if (!time.disabled) {
				const updatedValue = {...this.modelValue};
				updatedValue.time = time.value;
				this.$emit('update:modelValue', updatedValue);
			}
		},
		openOverride(slot) {
			if (!this.canManage) return;
			this.editOverride.slot = slot;
			// Preload selected with current closed IDs
			this.editOverride.selected = Array.isArray(slot.closedBoxIds) ? [...slot.closedBoxIds] : [];
			this.editOverride.error = '';
			this.editOverride.visible = true;
		},
		closeOverride() {
			this.editOverride.visible = false;
			this.editOverride.slot = null;
			this.editOverride.selected = [];
			this.editOverride.error = '';
		},
		isBoxInSlot(boxId) {
			// Box belongs to this slot if originalBoxIds includes it (includes both open and closed)
			if (!boxId) return false;
			if (!this.editOverride.slot || !Array.isArray(this.editOverride.slot.originalBoxIds)) return false;
			const ids = this.editOverride.slot.originalBoxIds.map(id => String(id));
			return ids.includes(String(boxId));
		},
		async saveOverride() {
			if (!this.canManage || !this.editOverride.slot) return;
			this.editOverride.saving = true;
			this.editOverride.error = '';
			try {
				const formData = new FormData();
				formData.append('action', 'mmk_booking_save_overrides');
				formData.append('nonce', window.mmkBookingData.adminNonce);
				formData.append('service_id', this.modelValue.service);
				formData.append('branch_id', this.modelValue.department);
				formData.append('date', this.modelValue.date);
				formData.append('time', this.editOverride.slot.value);
				// Append closed_box_ids
				(this.editOverride.selected || []).forEach(id => formData.append('closed_box_ids[]', id));

				const response = await fetch(window.mmkBookingData.ajaxUrl, { method: 'POST', body: formData });
				const data = await response.json();
				if (!data || !data.success) {
					throw new Error((data && data.data && data.data.message) ? data.data.message : 'Failed to save overrides');
				}

				// Close modal and request refresh
				this.closeOverride();
				this.$emit('refresh-availability');
			} catch (e) {
				console.error('Error saving overrides:', e);
				this.editOverride.error = String(e.message || e);
			} finally {
				this.editOverride.saving = false;
			}
		}
	},
	template: `
        <div class="mmk-booking-time-slots">
            <div v-if="canManage" class="mmk-admin-toggle" style="display:flex;margin-bottom:6px;gap:10px;">
                <label style="display:flex;align-items:center;gap:6px;cursor:pointer;">
                    <input type="checkbox" v-model="adminModeOn"> <span>Admin režīms</span>
                </label>
<br>                
                <button type="button" @click="saveAllSlotOverrides" class="button button--green button--small" v-if="adminModeOn">
                    Saglabāt visas izmaiņas
				</button>
            </div>
            <div class="mmk-booking-time-slots__grid">
                <div v-for="slot in formattedSlots" :key="slot.value" class="mmk-booking-time-slot__container">
                    <label 
                       :class="[
                           'mmk-booking-time-slot',
                           {
                               'mmk-booking-time-slot--disabled': slot.disabled,
                               'mmk-booking-time-slot--selected': slot.value === modelValue.time,
                               'mmk-booking-time-slot--past': slot.isPastOrTooSoon
                           }
                       ]"
                       @click="selectTime(slot)">
                        <input type="radio" class="mmk-booking-time-slot__input show-for-sr"
                               :value="slot.value" 
                               :name="'time-slot'"
                               :disabled="slot.disabled"
                               :checked="slot.value === modelValue.time">

                        <span v-if="slot.disabled" 
                              class="mmk-booking-time-slot__dot mmk-booking-time-slot__dot--disabled"></span>
                        <span v-else-if="slot.value === modelValue.time" 
                              class="mmk-booking-time-slot__dot mmk-booking-time-slot__dot--selected"></span>
                        <span v-else 
                              class="mmk-booking-time-slot__dot"></span>

                        <span class="mmk-booking-time-slot__time">
                            {{ slot.label }}
                        </span>
                        <button v-if="false && canManage"
                                type="button"
                                class="mmk-booking-time-slot__edit-btn"
                                title="Rediģēt laika vietas"
                                @click.stop.prevent="openOverride(slot)">⚙</button>
                    </label>

                    <!-- Inline Admin Override Panel -->
                    <div v-if="canManage && adminModeOn" class="mmk-booking-override-inline" @click.stop style="width:100%;margin:8px 0;">
						<div class="mmk-booking-override-boxes" :class="{'is-disabled': editOverride.saving}">
							<p class="mmk-booking-override-boxes__info">Brīvie boksi: <span class="mmk-booking-time-slot__counts"> ({{ slot.boxes }}/{{ slot.totalBoxes }})</span></p>
							
							<p class="mmk-booking-override-boxes__info">Aizvērtie boksi</p>
                        
                            <label v-for="(box, idx) in boxesForSlot(slot)" :key="box.id" class="mmk-booking-override-boxes__box">
                                <input type="checkbox"
                                       :value="box.id"
                                       :checked="selectionFor(slot).includes(String(box.id))"
                                       @change="toggleBox(String(box.id), slot)">
                                <span>{{ box.label || ('Box ' + box.id) }}</span>
                            </label>
                            <p v-if="!boxesForSlot(slot) || boxesForSlot(slot).length === 0">Šai filiālei nav definētu boksiem.</p>
                        </div>

                        <div class="mmk-booking-override-boxes__actions">
                            <button type="button" class="mmk-booking-override-boxes__button mmk-booking-override-boxes__button--save" :disabled="editOverride.saving" @click.stop.prevent="saveSlotOverride(slot)" title="Saglabāt">
                            Saglabāt
                            </button>
                            <button type="button" class="mmk-booking-override-boxes__button mmk-booking-override-boxes__button--close-all" :disabled="editOverride.saving" @click.stop.prevent="closeAll(slot)" title="Aizvērt visus">Aizvērt visus</button>
                            <button type="button" class="mmk-booking-override-boxes__button mmk-booking-override-boxes__button--open-all" :disabled="editOverride.saving" @click.stop.prevent="openAll(slot)" title="Atvērt visus">Atvērt visus</button>
                        </div>

                        <p v-if="editOverride.error" class="mmk-booking-override-boxes__error">{{ editOverride.error }}</p>
                    </div>
                </div>
            </div>
        </div>
    `
};

// Main Booking Form Component
const BookingForm = {
	components: {
		'car-details': CarDetails,
		'booking-calendar': BookingCalendar,
		'time-slots': TimeSlots
	},
	data() {
		return {
			i18n: mmkBookingData.i18n || {},
			initialLoading: false,
			isSubmitting: false,
			isSubmitted: false,
			submittedData: null,
			error: null,
			services: {},
			departments: {},
			availabilityData: {},
			disabledDates: [],
			availableSlots: {},
			timeSlots: [],
			formData: {
				service: '',
				additionalOption: '',
				department: '',
				date: '',
				time: '',
				car: {
					plateNr: '',
					brand: '',
					model: '',
					year: ''
				},
				name: '',
				phone: '',
				email: '',
				comment: '',
				termsAgree: false,
				newsletterAgree: false,
				checkAll: false
			},
			validationErrors: {
				showErrorMessage: false,
				sections: []
			},
			priceTablePopup: {
				isVisible: false,
				serviceId: null,
				content: ''
			}
		}
	},
	computed: {
		hasAdditionalOptions() {
			if (!this.formData.service || !this.services[this.formData.service]) {
				return false;
			}

			const service = this.services[this.formData.service];
			return service.hasAdditionalOptions &&
				service.additionalOptions &&
				service.additionalOptions.length > 0;
		},
		selectedService() {
			return this.services[this.formData.service] || null;
		},
		availableBranches() {
			// Return an object with branch keys and availability status
			if (!this.formData.service || !this.availabilityData[this.formData.service]) {
				// console.log('No availability data for service:', this.formData.service);
				return {};
			}

			const serviceData = this.availabilityData[this.formData.service];
			// console.log('Service availability data:', JSON.stringify(serviceData, null, 2));
			const result = {};

			// Check each branch if it has any available dates for the selected service
			Object.keys(this.departments).forEach(branchKey => {
				// First check if the branch has any boxes with this service
				const branch = this.departments[branchKey];
				const hasServiceInBoxes = branch.boxes && branch.boxes.some(box =>
					!box.disabled && box.services && box.services.includes(this.formData.service)
				);

				// Only consider the branch available if it has the service assigned to at least one box
				// AND the service has availability data for this branch
				const branchData = serviceData[branchKey];
				// console.log(`Branch ${branchKey} has service in boxes:`, hasServiceInBoxes);
				// console.log(`Branch ${branchKey} data:`, branchData ? 'exists' : 'does not exist');

				// Branch is available if:
				// 1. The service is assigned to at least one box in this branch
				// 2. The branch exists in the availability data for this service
				// 3. There is at least one date with availability
				result[branchKey] = hasServiceInBoxes &&
					branchData &&
					Object.values(branchData).some(dateInfo => dateInfo.available);

				// console.log(`Branch ${branchKey} availability:`, result[branchKey]);
			});

			// console.log('Final branch availability:', result);
			return result;
		}
	},
	watch: {
		'formData.termsAgree': function () {
			this.updateCheckAllStatus();
		},
		'formData.newsletterAgree': function () {
			this.updateCheckAllStatus();
		}
	},
	mounted() {
		// Load services and departments data
		this.loadData();
	},
	methods: {
		loadData() {
			this.initialLoading = true; // Use initialLoading for the overlay
			this.error = null;

			// Get data from the global variable
			if (window.mmkBookingData) {
				this.services = window.mmkBookingData.services;
				this.departments = window.mmkBookingData.branches;

				// Auto-select first service
				if (Object.keys(this.services).length > 0) {
					this.formData.service = Object.keys(this.services)[0];
					this.onServiceChange();
				}

				// Fetch all availability data
				this.fetchAllAvailability();

				// Note: initialLoading will be set to false after fetchAllAvailability completes
			} else {
				this.error = 'Failed to load booking data';
				this.initialLoading = false;
			}
		},
		async fetchAllAvailability(skipAutoSelect = false) {
			try {
				// Format date in YYYY-MM-DD format using local timezone
				const today = new Date();
				const year = today.getFullYear();
				const month = String(today.getMonth() + 1).padStart(2, '0');
				const day = String(today.getDate()).padStart(2, '0');
				const formattedDate = `${year}-${month}-${day}`;
				const daysToOffer = Number(mmkBookingData.daysToOffer || 90);

				// Calculate end date (90 days from today)
				const endDate = new Date(today);
				endDate.setDate(today.getDate() + daysToOffer);
				const endYear = endDate.getFullYear();
				const endMonth = String(endDate.getMonth() + 1).padStart(2, '0');
				const endDay = String(endDate.getDate()).padStart(2, '0');
				const formattedEndDate = `${endYear}-${endMonth}-${endDay}`;

				const formData = new FormData();
				formData.append('action', 'mmk_get_all_availability');
				formData.append('nonce', mmkBookingData.nonce);
				formData.append('start_date', formattedDate);
				formData.append('end_date', formattedEndDate);

				const response = await fetch(mmkBookingData.ajaxUrl, {
					method: 'POST',
					body: formData
				});

				const data = await response.json();

				if (data.success) {
					this.availabilityData = data.data.availability;
					this.updateAvailability();

					if (!skipAutoSelect && this.formData.service) {
						this.$nextTick(() => {
							// Only auto-select if department is not selected yet
							if (!this.formData.department) {
								this.selectFirstAvailableBranch();
								this.$nextTick(() => {
									if (this.formData.department && !this.formData.date) {
										this.selectFirstAvailableDate();
									}
								});
							}
						});
					}
				} else {
					console.error('Failed to fetch availability:', data.data);
				}
			} catch (error) {
				console.error('Error fetching availability:', error);
			} finally {
				// Add a small delay before turning off the loading overlay to ensure it's visible
				setTimeout(() => {
					this.initialLoading = false; // Set initialLoading to false after initial data load
				}, 1000); // 1 second delay
			}
		},
		updateAvailability() {
			if (!this.formData.service || !this.formData.department) {
				this.disabledDates = [];
				this.timeSlots = [];
				return;
			}

			const serviceData = this.availabilityData[this.formData.service] || {};
			const branchData = serviceData[this.formData.department] || {};

			// Update disabled dates
			this.disabledDates = Object.entries(branchData)
				.filter(([_, info]) => !info.available)
				.map(([date]) => date);

			// Update available slots for calendar
			this.availableSlots = Object.entries(branchData)
				.reduce((acc, [date, info]) => {
					if (info.available && info.timeSlots) {
						// Count total available boxes instead of just counting time slots
						acc[date] = info.timeSlots.reduce((sum, slot) => sum + slot.available_boxes, 0);
					}
					return acc;
				}, {});

			// Update time slots for selected date if we have one
			if (this.formData.date && branchData[this.formData.date]?.timeSlots) {
				this.timeSlots = branchData[this.formData.date].timeSlots;
			} else {
				this.timeSlots = [];
			}
		},
		onServiceChange() {
			// Reset related fields
			this.formData.additionalOption = '';
			const previousDepartment = this.formData.department;
			const previousDate = this.formData.date;
			this.formData.department = '';
			this.formData.date = '';
			this.formData.time = '';

			// If the selected service has additional options, auto-select the first one
			if (this.formData.service && this.hasAdditionalOptions) {
				const service = this.services[this.formData.service];
				if (service.additionalOptions && service.additionalOptions.length > 0) {
					this.formData.additionalOption = service.additionalOptions[0].value;
					// console.log('Auto-selected additional option:', this.formData.additionalOption);
				}
			}

			// Update availability for new selection
			this.updateAvailability();

			// After availability is updated, select the first available branch
			this.$nextTick(() => {
				this.selectFirstAvailableBranch();

				// If a date was previously selected, try to reselect it
				if (previousDate) {
					this.$nextTick(() => {
						// Check if the previously selected date is available for the new service/branch
						const serviceData = this.availabilityData[this.formData.service] || {};
						const branchData = serviceData[this.formData.department] || {};

						if (branchData[previousDate]?.available) {
							this.formData.date = previousDate;
							this.onDateChange({date: previousDate});
						}
					});
				}
			});
		},
		onDepartmentChange() {
			// Save the previously selected date
			const previousDate = this.formData.date;

			// Reset date-related fields
			this.formData.date = '';
			this.formData.time = '';

			// Update availability for new selection
			this.updateAvailability();

			// If a date was previously selected, try to reselect it
			if (previousDate) {
				this.$nextTick(() => {
					// Check if the previously selected date is available for the new branch
					const serviceData = this.availabilityData[this.formData.service] || {};
					const branchData = serviceData[this.formData.department] || {};

					if (branchData[previousDate]?.available) {
						this.formData.date = previousDate;
						this.onDateChange({date: previousDate});
					}
				});
			}
		},
		onDateChange(formData) {
			// Update time slots for the selected date
			const serviceData = this.availabilityData[this.formData.service] || {};
			const branchData = serviceData[this.formData.department] || {};
			this.timeSlots = branchData[formData.date]?.timeSlots || [];

			// Update the date in formData
			this.formData.date = formData.date;
			this.formData.time = '';  // Reset time when date changes

			// Auto-select the first available time slot if any are available
			this.$nextTick(() => {
				// Get current date and time
				const now = new Date();
				// Add 2 hours to current time
				const minTime = new Date(now.getTime() + (2 * 60 * 60 * 1000));

				// Filter slots that are available and not in the past or within next 2 hours
				const availableSlots = this.timeSlots.filter(slot => {
					if (slot.available_boxes <= 0) return false;

					// Parse the time from the slot
					const [hours, minutes] = slot.time.split(':').map(Number);

					// Create a date object for the slot time on the selected date
					const slotDate = new Date(this.formData.date);
					slotDate.setHours(hours, minutes, 0, 0);

					// Check if the slot is in the past or within the next 2 hours
					return slotDate >= minTime;
				});

				if (availableSlots.length > 0) {
					// We have valid time slots, select the first one
					this.formData.time = availableSlots[0].time;
				} else {
					// No valid time slots for this date, try to find the next available date
					this.findNextAvailableDate();
				}
			});
		},

		findNextAvailableDate() {
			if (!this.formData.service || !this.formData.department || !this.formData.date) {
				return;
			}

			const serviceData = this.availabilityData[this.formData.service] || {};
			const branchData = serviceData[this.formData.department] || {};

			// Get all available dates after the current selected date
			const availableDates = Object.entries(branchData)
				.filter(([date, info]) => info.available && date > this.formData.date)
				.map(([date]) => date)
				.sort(); // Sort dates chronologically

			// Check each date for valid time slots
			for (const date of availableDates) {
				if (this.hasValidTimeSlotsForDate(date, branchData)) {
					// console.log(`Moving to next available date: ${date}`);
					// Update the formData directly to ensure the calendar gets updated
					this.formData.date = date;

					// Call onDateChange to update time slots, but prevent infinite recursion
					this.timeSlots = branchData[date]?.timeSlots || [];

					// Auto-select the first available time slot
					const availableSlots = this.getValidTimeSlotsForDate(date);
					if (availableSlots.length > 0) {
						this.formData.time = availableSlots[0].time;
					}

					return; // Exit after finding the first valid date
				}
			}
		},

		getValidTimeSlotsForDate(date) {
			const serviceData = this.availabilityData[this.formData.service] || {};
			const branchData = serviceData[this.formData.department] || {};
			const timeSlots = branchData[date]?.timeSlots || [];

			// Get current date and time
			const now = new Date();
			// Add 2 hours to current time
			const minTime = new Date(now.getTime() + (2 * 60 * 60 * 1000));

			// Filter slots that are available and not in the past or within next 2 hours
			return timeSlots.filter(slot => {
				if (slot.available_boxes <= 0) return false;

				// Parse the time from the slot
				const [hours, minutes] = slot.time.split(':').map(Number);

				// Create a date object for the slot time on the selected date
				const slotDate = new Date(date);
				slotDate.setHours(hours, minutes, 0, 0);

				// Check if the slot is not in the past or within the next 2 hours
				return slotDate >= minTime;
			});
		},

		hasValidTimeSlotsForDate(date, branchData) {
			// Get time slots for this date
			const timeSlots = branchData[date]?.timeSlots || [];

			// Get current date and time
			const now = new Date();
			// Add 2 hours to current time
			const minTime = new Date(now.getTime() + (2 * 60 * 60 * 1000));

			// Check if there are any available slots that are not within the next 2 hours
			return timeSlots.some(slot => {
				if (slot.available_boxes <= 0) return false;

				// Parse the time from the slot
				const [hours, minutes] = slot.time.split(':').map(Number);

				// Create a date object for the slot time on the selected date
				const slotDate = new Date(date);
				slotDate.setHours(hours, minutes, 0, 0);

				// Check if the slot is not in the past or within the next 2 hours
				return slotDate >= minTime;
			});
		},
		async onSubmit(e) {
			e.preventDefault();

			if (this.isSubmitting) {
				return; // Prevent multiple submissions
			}

			// Validate form fields
			if (!this.validateForm()) {
				return;
			}

			this.isSubmitting = true;

			try {
				const formData = {
					...this.formData,
					car: JSON.stringify(this.formData.car)
				};

				const response = await jQuery.ajax({
					url: mmkBookingData.ajaxUrl,
					method: 'POST',
					data: {
						action: 'mmk_submit_booking',
						nonce: mmkBookingData.nonce,
						...formData
					}
				});

				if (response.success) {
					// Store submitted data for display
					this.submittedData = {
						service: this.services[this.formData.service]?.label || '',
						additionalOption: this.formData.additionalOption ?
							this.services[this.formData.service]?.additionalOptions.find(opt => opt.value === this.formData.additionalOption)?.label || '' : '',
						department: this.departments[this.formData.department]?.label || '',
						departmentAddress: this.departments[this.formData.department]?.address || '',
						date: this.formData.date,
						time: this.formData.time,
						name: this.formData.name,
						phone: this.formData.phone,
						email: this.formData.email,
						car: this.formData.car,
						comment: this.formData.comment || '-'
					};

					// Set submission status to true
					this.isSubmitted = true;

					// Scroll to the success message after a short delay to ensure it's rendered
					this.$nextTick(() => {
						setTimeout(() => {
							const successMessage = document.querySelector('.mmk-booking-form__success-message');
							if (successMessage) {
								// Get the element's position
								const rect = successMessage.getBoundingClientRect();
								const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

								// Scroll to the element with an offset of 50px (adjust as needed)
								const offsetPosition = scrollTop + rect.top - 100;

								window.scrollTo({
									top: offsetPosition,
									behavior: 'smooth'
								});
							}
						}, 100);
					});
				} else {
					alert(response.data.message || 'Kļūda, iesniedzot pierakstu');
				}
			} catch (error) {
				console.error('Booking submission error:', error);
				alert('Kļūda, iesniedzot pierakstu');
			} finally {
				this.isSubmitting = false;
			}
		},

		validateForm() {
			// Reset validation errors
			this.validationErrors.sections = [];
			this.validationErrors.showErrorMessage = false;

			let hasErrors = false;

			// Check required fields
			if (!this.formData.service) {
				this.validationErrors.sections.push('service');
				hasErrors = true;
			}

			if (this.hasAdditionalOptions && !this.formData.additionalOption) {
				this.validationErrors.sections.push('additionalOption');
				hasErrors = true;
			}

			if (!this.formData.department) {
				this.validationErrors.sections.push('department');
				hasErrors = true;
			}

			if (!this.formData.date) {
				this.validationErrors.sections.push('date');
				hasErrors = true;
			}

			if (!this.formData.time) {
				this.validationErrors.sections.push('time');
				hasErrors = true;
			}

			// Validate car details
			const car = this.formData.car;
			if (!car.plateNr || !car.brand || !car.model || !car.year) {
				this.validationErrors.sections.push('car');
				hasErrors = true;
			}

			// Validate client details
			if (!this.formData.name) {
				this.validationErrors.sections.push('name');
				hasErrors = true;
			}

			if (!this.formData.phone) {
				this.validationErrors.sections.push('phone');
				hasErrors = true;
			}

			if (!this.formData.email) {
				this.validationErrors.sections.push('email');
				hasErrors = true;
			}

			if (!this.formData.termsAgree) {
				this.validationErrors.sections.push('termsAgree');
				hasErrors = true;
			}

			// Show error message if there are any validation errors
			this.validationErrors.showErrorMessage = hasErrors;

			return !hasErrors;
		},
		resetForm() {
			this.formData = {
				service: '',
				additionalOption: '',
				department: '',
				date: '',
				time: '',
				car: {
					plateNr: '',
					brand: '',
					model: '',
					year: ''
				},
				name: '',
				phone: '',
				email: '',
				comment: '',
				termsAgree: false,
				newsletterAgree: false,
				checkAll: false
			};
			this.checkAll = false;
		},
		selectFirstAvailableBranch() {
			// Find the first available branch for the selected service
			const availableBranchKeys = Object.keys(this.availableBranches).filter(key => this.availableBranches[key]);

			if (availableBranchKeys.length > 0) {
				this.formData.department = availableBranchKeys[0];
				this.onDepartmentChange();

				// After department is selected, find the first available date
				this.$nextTick(() => {
					this.selectFirstAvailableDate();
				});
			}
		},
		selectFirstAvailableDate() {
			if (!this.formData.service || !this.formData.department) {
				return;
			}

			const serviceData = this.availabilityData[this.formData.service] || {};
			const branchData = serviceData[this.formData.department] || {};

			// Find all available dates
			const availableDates = Object.entries(branchData)
				.filter(([_, info]) => info.available)
				.map(([date]) => date)
				.sort(); // Sort dates chronologically

			if (availableDates.length > 0) {
				// Check each date for valid time slots (not within 2 hours)
				for (const date of availableDates) {
					if (this.hasValidTimeSlotsForDate(date, branchData)) {
						// Update the formData directly to ensure the calendar gets updated
						this.formData.date = date;

						// Call onDateChange to update time slots
						this.onDateChange({date});
						return; // Exit after finding the first valid date
					}
				}

				// If we get here, there were no dates with valid time slots
				// Just select the first available date as a fallback
				const firstAvailableDate = availableDates[0];
				this.formData.date = firstAvailableDate;
				this.onDateChange({date: firstAvailableDate});
			}
		},
		onRefreshAvailability() {
			// Refresh availability without auto-selecting branch/date to preserve current selection
			this.fetchAllAvailability(true);
		},
		showPriceTable(serviceId) {
			if (!serviceId || !this.services[serviceId]) {
				return;
			}

			this.priceTablePopup.serviceId = serviceId;
			this.priceTablePopup.content = this.services[serviceId].priceTableContent || '<p>Nav pieejama cenu tabula.</p>';
			this.priceTablePopup.isVisible = true;
		},
		closePriceTable() {
			this.priceTablePopup.isVisible = false;
			this.priceTablePopup.serviceId = null;
			this.priceTablePopup.content = '';
		},
		toggleAllCheckboxes() {
			if (this.formData.checkAll) {
				this.formData.termsAgree = true;
				this.formData.newsletterAgree = true;
			} else {
				this.formData.termsAgree = false;
				this.formData.newsletterAgree = false;
			}
		},
		updateCheckAllStatus() {
			this.formData.checkAll = this.formData.termsAgree && this.formData.newsletterAgree;
		},
		formatLocalDate(dateString) {
			if (!dateString) return '';

			// Parse the date (which is in YYYY-MM-DD format)
			const parts = dateString.split('-');
			if (parts.length !== 3) return dateString;

			// Format as DD.MM.YYYY to match the local format
			return `${parts[2]}.${parts[1]}.${parts[0]}`;
		}
	},
	template: `<div class="mmk-booking-form">
	<div v-if="initialLoading" class="mmk-booking-form__loading-overlay">
		<div>
			<div class="mmk-booking-form__spinner"></div>
		</div>
	</div>
	
	<!-- Success Message -->
	<div v-if="isSubmitted" class="mmk-booking-form__success-message">
		<h2>{{ i18n.success_title }}</h2>
		<table class="mmk-booking-form__success-table">
		<tbody>
			<tr>
				<th>{{ i18n.service_label }}</th>
				<td>{{ submittedData.service }}<br>{{ submittedData.additionalOption ? '(' + submittedData.additionalOption + ')' : '' }}</td>
			</tr>
			<tr>
				<th>{{ i18n.branch_label }}</th>
				<td>{{ submittedData.departmentAddress }} ({{ submittedData.department }})</td>
			</tr>
			<tr>
				<th>{{ i18n.date_time_label }}</th>
				<td>{{ formatLocalDate(submittedData.date) }} {{ submittedData.time }}</td>
			</tr>
			<tr>
				<th>{{ i18n.client_info_label }}</th>
				<td>{{ submittedData.name }}, {{ submittedData.phone }}, {{ submittedData.email }}</td>
			</tr>
			<tr>
				<th>{{ i18n.vehicle_label }}</th>
				<td>{{ submittedData.car.plateNr }}, {{ submittedData.car.brand }}, {{ submittedData.car.model }}, {{ submittedData.car.year }}</td>
			</tr>
			<tr>
				<th>{{ i18n.additional_info_label }}</th>
				<td>{{ submittedData.comment }}</td>
			</tr>
			</tbody>
		</table>
	</div>
	
	<!-- Booking Form -->
	<form v-if="!isSubmitted" @submit.prevent="onSubmit" id="booking-form">
		<div class="mmk-booking-form__section" :class="{'mmk-booking-form__section--error': validationErrors.sections.includes('service')}">
			<label class="mmk-booking-form__label">{{ i18n.service }}</label>
			<div class="mmk-booking-form__services-list">
				<div 
					v-for="(service, id) in services" 
					:key="id" 
					:class="['mmk-booking-form__service-option', { 'mmk-booking-form__service-option--selected': formData.service === id }]">
					<label class="mmk-booking-form__service-label">
						<input 
							type="radio" 
							v-model="formData.service" 
							:value="id" 
							@change="onServiceChange"
							class="mmk-booking-form__service-radio show-for-sr">
						<span class="mmk-booking-form__service-dot"></span>
						<span class="mmk-booking-form__service-name">{{ service.label }}</span>
						<a 
							v-if="service.priceTableContent" 
							href="#" 
							class="mmk-booking-form__service-prices"
							@click.prevent="showPriceTable(id)">
							{{ i18n.prices }}
						</a>
					</label>
					
					<div 
						v-if="formData.service === id && service.hasAdditionalOptions && service.additionalOptions && service.additionalOptions.length > 0" 
						:class="['mmk-booking-form__additional-options', { 'mmk-booking-form__additional-options--selected': formData.additionalOption }, {'mmk-booking-form__additional-options--error': validationErrors.sections.includes('additionalOption')}]">
						<div 
							v-for="option in service.additionalOptions" 
							:key="option.value" 
							:class="['mmk-booking-form__additional-option', { 'mmk-booking-form__additional-option--selected': formData.additionalOption === option.value }]">
							<label class="mmk-booking-form__option-label">
								<input 
									type="radio" 
									v-model="formData.additionalOption" 
									:value="option.value"
									class="mmk-booking-form__option-radio show-for-sr">
								<span class="mmk-booking-form__option-dot"></span>
								<span class="mmk-booking-form__option-name">{{ option.label }}</span>
							</label>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="mmk-booking-form__section mmk-booking-form__section--departments" :class="{'mmk-booking-form__section--error': validationErrors.sections.includes('department')}">
			<label class="mmk-booking-form__label">{{ i18n.branch }}</label>
			<label 
				v-for="(dept, key) in departments" 
				:key="key" 
				:class="[
					'mmk-booking-form-department', 
					{'mmk-booking-form-department--disabled': formData.service && availableBranches[key] === false},
					{'mmk-booking-form-department--selected': formData.department === key}
				]">
				<span class="mmk-booking-form-department__radio">
					<input 
						type="radio" 
						v-model="formData.department" 
						:value="key" 
						@change="onDepartmentChange"
						:disabled="formData.service && availableBranches[key] === false"
						class="show-for-sr">
					<span class="mmk-booking-form-department__dot"></span>
				</span>
				<span class="mmk-booking-form-department__label">
					<span class="mmk-booking-form__department-label">{{ dept.address }}</span>
					<span class="mmk-booking-form__department-address">{{ dept.label }}</span>
				</span>
			</label>
		</div>

		<div class="mmk-booking-form__section" :class="{'mmk-booking-form__section--error': validationErrors.sections.includes('date')}">
			<label class="mmk-booking-form__label">{{ i18n.date }}</label>
			<div class="mmk-booking-form__info-note">
	<p>
	{{ i18n.info_note_1 }}
	</p>
	<ul>
	<li>{{ i18n.contact_riga_plaza }}<a href="tel:+37123204444">+37123204444</a></li>
	<li>{{ i18n.contact_mezciems }}<a href="tel:+37126265050">+37126265050</a></li>
	</ul>
	<p>
		{{ i18n.info_note_2 }}
	</p>
</div>
			<div class="row">
				<div class="small-12 columns">
					<booking-calendar 
						v-model="formData"
						:available-slots="availableSlots"
						:disabled-dates="disabledDates"
						@update:modelValue="onDateChange" />
				</div>
			</div>
		</div>
		
		<div class="mmk-booking-form__section" :class="{'mmk-booking-form__section--error': validationErrors.sections.includes('time')}">
			<label class="mmk-booking-form__label">{{ i18n.time }}</label>
			<div class="row">
				<div class="small-12 columns">
					<div class="mmk-booking-time">
						<time-slots 
							v-model="formData"
							:available-slots="timeSlots"
							:current-branch-boxes="departments[formData.department]?.boxes || []"
							@refresh-availability="onRefreshAvailability" />
					</div>
				</div>
			</div>
		</div>

		<div class="mmk-booking-form__section mmk-booking-form__section--client" :class="{'mmk-booking-form__section--error': validationErrors.sections.includes('name') || validationErrors.sections.includes('phone') || validationErrors.sections.includes('email')}">
			<div class="row collapse">
				<div class="small-12 column">
					<label class="mmk-booking-form__label">{{ i18n.client_info }}</label>
				</div>
				<div class="small-12 medium-4 columns">
					<div class="mmk-booking-form__input-group">
						<input 
							type="text" 
							v-model="formData.name" 
							placeholder="" 
							autocomplete="name"
							:class="['mmk-booking-form__text-input', formData.name ? 'has-content' : '']">
						<label class="mmk-booking-form__floating-label">{{ i18n.name }}</label>
					</div>
				</div>
				<div class="small-12 medium-4 columns mmk-booking-form__column mmk-booking-form__column--phone">
					<div class="mmk-booking-form__input-group">
						<input 
							type="text" 
							v-model="formData.phone" 
							placeholder="" 
							autocomplete="tel"
							:class="['mmk-booking-form__text-input mmk-booking-form__text--input-phone', formData.phone ? 'has-content' : '']">
						<label class="mmk-booking-form__floating-label">{{ i18n.phone }}</label>
					</div>
				</div>
				<div class="small-12 medium-4 columns">
					<div class="mmk-booking-form__input-group">
						<input 
							type="text" 
							v-model="formData.email" 
							placeholder="" 
							autocomplete="email"
							:class="['mmk-booking-form__text-input', formData.email ? 'has-content' : '']">
						<label class="mmk-booking-form__floating-label">{{ i18n.email }}</label>
					</div>
				</div>
			</div>
		</div>
		
		<car-details v-model="formData.car" :validation-errors="validationErrors" />
		
		<div class="mmk-booking-form__section mmk-booking-form__section--comment">
			<label class="mmk-booking-form__label">{{ i18n.additional_info_label }}</label>
		
			<div class="row">
				<div class="column">
					<div class="mmk-booking-form__input-group">
						<textarea 
							v-model="formData.comment" 
							placeholder="" 
							rows="4" 
							:class="['mmk-booking-form__text-input', formData.comment ? 'has-content' : '']">
						</textarea>
						<label class="mmk-booking-form__floating-label">{{ i18n.comment }}</label>
					</div>
				</div>
			</div>
		</div>
		
		<div class="mmk-booking-form__section mmk-booking-form__section--submit">
			<div class="mmk-booking-form__submit-agree">
				<div class="row">
					<div class="small-12 columns">
						<label class="mmk-booking-form__label mmk-booking-form__label--checkbox">
							<input 
								type="checkbox" 
								v-model="formData.termsAgree">
							{{ i18n.agree_terms }}&nbsp;<a href="https://mmkriepas.lv/privatuma-politika/" target="_blank">{{ i18n.terms }}</a>
						</label>
					</div>
					<div class="small-12 columns">
						<label class="mmk-booking-form__label mmk-booking-form__label--checkbox">
							<input 
								type="checkbox" 
								v-model="formData.newsletterAgree">
							{{ i18n.receive_offers }}
						</label>
					</div>
				</div>
				<div class="small-12 columns">
					<label class="mmk-booking-form__label mmk-booking-form__label--checkbox mmk-booking-form__label--check-all">
						<input 
							type="checkbox" 
							v-model="formData.checkAll"
							@change="toggleAllCheckboxes">
						{{ i18n.check_all }}
					</label>
				</div>
			</div>

			<div class="mmk-booking__submit-container">
				<div>
					<button 
						type="submit" 
						class="button button--green mmk-booking-form__submit" 
						:disabled="isSubmitting">
						<span v-if="isSubmitting">{{ i18n.submitting }}</span>
						<span v-else>{{ i18n.submit }}</span>
					</button>
				</div>   
				
				<p class="mmk-booking__error-message" v-if="validationErrors.showErrorMessage">
					{{ i18n.fill_all_fields }}
				</p>
			</div>   
		</div>
	</form>
	
	<!-- Price Table Popup -->
	<div 
		v-if="priceTablePopup.isVisible" 
		class="mmk-booking-price-table-popup">
		<div 
			class="mmk-booking-price-table-popup__overlay" 
			@click="closePriceTable"></div>
		<div class="mmk-booking-price-table-popup__content">
			<button 
				type="button" 
				class="mmk-booking-price-table-popup__close" 
				@click="closePriceTable">×
			</button>
			<h3 
				class="mmk-booking-price-table-popup__title" 
				v-if="priceTablePopup.serviceId && services[priceTablePopup.serviceId]">
				{{ services[priceTablePopup.serviceId].label }}{{ i18n.prices_for }}
			</h3>
			
			<div class="mmk-booking-price-table-popup__table" v-html="priceTablePopup.content"></div>
		</div>
	</div>
</div>`
};

// Initialize Vue app
document.addEventListener('DOMContentLoaded', () => {
	if (typeof Vue !== 'undefined') {
		const app = Vue.createApp({
			components: {
				'booking-form': BookingForm
			},
			template: '<booking-form />'
		});

		app.mount('#mmk-booking-app');
	} else {
		console.error('Vue is not available.');
	}
});
